/**
 * DevModeBadge
 * ------------
 * Small always-on-top pill shown while the local dev login bypass is active,
 * so it is never ambiguous which fake role you are viewing. Clicking it opens
 * DevPanel, which holds the forced-expiry switches, a crash test and the
 * persona switch. Renders nothing unless a dev role is currently selected (and
 * thus never in production builds).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip, Text } from '../ui';
import { withAlpha, useTheme } from '../ui';
import { TuneRounded as TuneIcon } from '../ui/icons';
import {
  DEV_LOGIN_ENABLED,
  devRoleOption,
  getDevRole,
} from '../auth/devAuth';
import { getForcedExpiry } from '../auth/devSession';
import { RADIUS } from '../ui/theme/tokens';
import DevPanel from './DevPanel';

export default function DevModeBadge() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const role = getDevRole();

  if (!DEV_LOGIN_ENABLED || !role) return null;

  const option = devRoleOption(role);
  // Armed is worth seeing without opening the panel: 'always' outlives a reload
  // and is the state most likely to be forgotten about.
  const armed = getForcedExpiry() !== 'off';

  const toggle = () => setOpen((wasOpen) => !wasOpen);

  return (
    <>
      {open && <DevPanel onClose={() => setOpen(false)} />}
    {/* Empty while the panel is open: MUI renders it above the panel, over the
        last button. */}
    <Tooltip title={open ? '' : 'Development options'} placement="top">
      <Box
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') toggle();
        }}
        vendorStyles={{
          position: 'fixed',
          bottom: 10,
          right: 12,
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: 'pointer',
          px: 1.25,
          py: 0.5,
          borderRadius: `${RADIUS.xs}px`,
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 2px 10px ${withAlpha(theme.palette.text.primary, 0.12)}`,
          transition: 'border-color .15s ease',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <Box
          styles={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: armed ? 'error.main' : 'warning.main',
            flexShrink: 0,
          }}
        />
        <Text
          level="caption"
          styles={{ fontWeight: 700, letterSpacing: '0.6px', color: 'text.secondary' }}
        >
          DEV
        </Text>
        <Text level="caption" styles={{ fontWeight: 600, color: 'text.primary' }}>
          {t(option.labelKey)}
        </Text>
        <TuneIcon styles={{ fontSize: '1rem', color: 'text.disabled' }} />
      </Box>
    </Tooltip>
    </>
  );
}
